import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { BEHAVIOR_LABELS, FREQUENCY_LABELS, describeAmount, describeSchedule, versionAt, type Behavior, type Concept, type ConceptVersion, WEEKDAY_NAMES_ES } from '@balance/domain';
import { api, type ConceptDetail } from '../api.js';
import { ConceptWizard, type WizardMode } from '../components/ConceptWizard.js';
import { Modal } from '../components/Modal.js';
import { Badge, ConceptName, Empty } from '../components/ui.js';
import { dateLabel, eur, periodLabel, shortDateLabel } from '../format.js';
import { useApp } from '../state.js';
import { AdjustModal } from './Projection.js';

type Tab = 'todos' | 'ingresos' | 'presupuestos' | 'compromisos' | 'financiacion';
const TABS: Array<[Tab, string, string]> = [
  ['todos', 'Todos', 'Lista central de conceptos'],
  ['ingresos', 'Ingresos', 'Salarios recurrentes, ajustes por mes y extraordinarios'],
  ['presupuestos', 'Presupuestos', 'Conceptos que se consumen por período'],
  ['compromisos', 'Compromisos', 'Sesiones, suscripciones y servicios'],
  ['financiacion', 'Financiación', 'Préstamos recibidos y compras en cuotas'],
];

function tabOf(c: Concept): Tab[] {
  const tabs: Tab[] = ['todos'];
  if (c.direction === 'income') tabs.push('ingresos');
  if (c.behavior === 'budget') tabs.push('presupuestos');
  if (c.behavior === 'fixed' || c.behavior === 'variable_service' || c.behavior === 'occurrence') tabs.push('compromisos');
  if (c.behavior === 'loan' || (c.behavior === 'one_off' && true)) tabs.push('financiacion');
  return tabs;
}

export function ConceptsPage() {
  const { catalog, balance } = useApp();
  const params = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const tab = (params.tab as Tab | undefined) ?? 'todos';
  const [state, setState] = useState<'all' | 'active' | 'scheduled' | 'inactive'>('all');
  const [category, setCategory] = useState('all');
  const [behavior, setBehavior] = useState<'all' | Behavior>('all');
  const [wizard, setWizard] = useState<{ behavior?: Behavior; direction?: Concept['direction'] } | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  useEffect(() => {
    const id = search.get('editar');
    if (id) {
      setEditing(id);
      search.delete('editar');
      setSearch(search, { replace: true });
    }
  }, [search, setSearch]);
  const rows = useMemo(() => {
    if (!catalog || !balance) return [];
    return catalog.concepts
      .map((c) => {
        const versions = catalog.versions.filter((v) => v.conceptId === c.id);
        const row = balance.rows.find((r) => r.conceptId === c.id);
        const current = versionAt(versions, balance.today);
        const futureActive = versions.some((v) => v.validFrom > balance.today && v.state === 'active');
        const status: 'active' | 'scheduled' | 'inactive' = row?.activeNow ? 'active' : futureActive ? 'scheduled' : 'inactive';
        const lastActive = [...versions].reverse().find((v) => v.state === 'active') ?? null;
        return { concept: c, versions, row, current, status, lastActive };
      })
      .filter((x) => tabOf(x.concept).includes(tab))
      .filter((x) => state === 'all' || x.status === state)
      .filter((x) => category === 'all' || (x.concept.categoryId ?? 'none') === category)
      .filter((x) => behavior === 'all' || x.concept.behavior === behavior);
  }, [catalog, balance, tab, state, category, behavior]);
  if (!catalog || !balance) return <div className="skeleton" />;
  const tabInfo = TABS.find((t) => t[0] === tab)!;
  return (
    <div className="page">
      <div className="tabs" role="tablist">
        {TABS.map(([key, label]) => (
          <Link key={key} role="tab" aria-selected={tab === key} className={`button${tab === key ? ' on' : ''}`} to={key === 'todos' ? '/conceptos' : `/conceptos/${key}`}>
            {label}
          </Link>
        ))}
      </div>
      <div className="panel">
        <div className="panelhead">
          <div>
            <h2>{tabInfo[1]}</h2>
            <div className="muted">{tabInfo[2]}. El nombre abre la ficha de reglas; «Editar» cambia condiciones con fecha de vigencia.</div>
          </div>
          <div className="inline">
            {tab === 'ingresos' ? (
              <button type="button" className="button primary" onClick={() => setWizard({ behavior: 'income', direction: 'income' })}>
                + Nuevo ingreso
              </button>
            ) : (
              <button type="button" className="button primary" onClick={() => setWizard({ behavior: tab === 'presupuestos' ? 'budget' : tab === 'financiacion' ? 'loan' : tab === 'compromisos' ? 'fixed' : undefined })}>
                + Nuevo concepto
              </button>
            )}
          </div>
        </div>
        <div className="filters">
          <select aria-label="Estado" value={state} onChange={(e) => setState(e.target.value as typeof state)}>
            <option value="all">Activos, programados e inactivos</option>
            <option value="active">Activos</option>
            <option value="scheduled">Programados</option>
            <option value="inactive">Inactivos</option>
          </select>
          <select aria-label="Categoría" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="all">Todas las categorías</option>
            {catalog.categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
            <option value="none">Sin categoría</option>
          </select>
          <select aria-label="Comportamiento" value={behavior} onChange={(e) => setBehavior(e.target.value as typeof behavior)}>
            <option value="all">Todos los comportamientos</option>
            {(Object.keys(BEHAVIOR_LABELS) as Behavior[]).map((b) => (
              <option key={b} value={b}>
                {BEHAVIOR_LABELS[b]}
              </option>
            ))}
          </select>
        </div>
        <div className="tablewrap">
          <table>
            <thead>
              <tr>
                <th>Concepto</th>
                <th>Comportamiento</th>
                <th>Condiciones vigentes</th>
                <th className="number">{periodLabel(balance.currentPeriod, 'short')}</th>
                <th>Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <Empty>No hay conceptos con esos filtros.</Empty>
                  </td>
                </tr>
              ) : null}
              {rows.map(({ concept, current, status, row, lastActive }) => {
                const shown = current?.state === 'active' ? current : lastActive;
                const cell = row?.cells[balance.currentPeriod];
                return (
                  <tr key={concept.id}>
                    <td>
                      <ConceptName id={concept.id} name={concept.name} />
                      <div className="muted">{catalog.categories.find((c) => c.id === concept.categoryId)?.name ?? 'Sin categoría'}{concept.personId ? ` · ${catalog.persons.find((p) => p.id === concept.personId)?.name ?? ''}` : ''}</div>
                    </td>
                    <td>
                      {BEHAVIOR_LABELS[concept.behavior]}
                      {shown?.frequency ? <div className="muted">{FREQUENCY_LABELS[shown.frequency]}</div> : null}
                    </td>
                    <td>
                      {shown ? (
                        <>
                          {describeAmount(shown, concept.behavior)}
                          <div className="muted">
                            {describeSchedule(shown.frequency, shown.schedule, shown.validFrom)} · desde {shown.validFrom}
                            {current?.state === 'inactive' ? ` · baja desde ${current.validFrom}` : ''}
                          </div>
                        </>
                      ) : (
                        <span className="muted">Sin condiciones</span>
                      )}
                    </td>
                    <td className="number">{cell && cell.status !== 'inactive' ? eur(cell.projectedCents) : <span className="muted">—</span>}</td>
                    <td>
                      <Badge kind={status === 'active' ? '' : status === 'scheduled' ? 'warn' : 'neutral'}>{status === 'active' ? 'Activo' : status === 'scheduled' ? 'Programado' : 'Inactivo'}</Badge>
                    </td>
                    <td>
                      <button type="button" className="button small" onClick={() => setEditing(concept.id)}>
                        Editar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {wizard ? (
        <ConceptWizard
          mode="create"
          presetBehavior={wizard.behavior}
          presetDirection={wizard.direction}
          onClose={() => setWizard(null)}
          onSaved={(id) => {
            setWizard(null);
            setEditing(id);
            if (tab !== 'todos') navigate('/conceptos');
          }}
        />
      ) : null}
      {editing ? <ConceptEditor id={editing} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

/** Configuración central de un concepto: datos, condiciones por fecha, baja/reactivación, sesiones. */
export function ConceptEditor({ id, onClose }: { id: string; onClose: () => void }) {
  const { catalog, balance, run } = useApp();
  const [detail, setDetail] = useState<ConceptDetail | null>(null);
  const [wizard, setWizard] = useState<WizardMode | null>(null);
  const [deactivate, setDeactivate] = useState(false);
  const [adjust, setAdjust] = useState(false);
  const [session, setSession] = useState<{ key: string; date: string; mode: 'move' | 'reorganize' | 'add' } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const reload = () => api.concept(id).then(setDetail).catch(() => undefined);
  useEffect(() => {
    void reload();
  }, [id, catalog]); // eslint-disable-line react-hooks/exhaustive-deps
  const concept = catalog?.concepts.find((c) => c.id === id);
  const [form, setForm] = useState({ name: '', categoryId: '', personId: '', meter: false, notes: '' });
  useEffect(() => {
    if (concept) setForm({ name: concept.name, categoryId: concept.categoryId ?? '', personId: concept.personId ?? '', meter: concept.meter, notes: concept.notes ?? '' });
  }, [concept?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!concept || !catalog || !balance) return null;
  const versions = detail?.versions ?? catalog.versions.filter((v) => v.conceptId === id);
  const current = versionAt(versions, balance.today);
  const inactiveNow = !current || current.state === 'inactive';
  const lastActive = [...versions].reverse().find((v) => v.state === 'active') ?? null;
  const upcoming = (detail?.occurrences ?? []).filter((o) => o.date >= balance.today).slice(0, 12);
  const hasCalendar = concept.behavior !== 'budget';
  const hasAllocations = (balance.rows.find((r) => r.conceptId === id)?.cells ?? {}) && Object.values(balance.rows.find((r) => r.conceptId === id)?.cells ?? {}).some((c) => c.allocationIds.length > 0);
  const saveBasics = () => run(() => api.updateConcept(id, { name: form.name.trim(), categoryId: form.categoryId || null, personId: form.personId || null, meter: form.meter, notes: form.notes || null }), 'Datos guardados');

  return (
    <>
      <Modal title={`Configurar · ${concept.name}`} subtitle={`${BEHAVIOR_LABELS[concept.behavior]} · ${concept.direction === 'income' ? 'ingreso' : 'egreso'}. Los cambios de comportamiento se versionan por fecha; el histórico no se borra.`} onClose={onClose} side>
        <h3>Datos</h3>
        <div className="field-row">
          <label>
            Nombre
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </label>
          <label>
            Categoría
            <select value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}>
              <option value="">Sin categoría</option>
              {catalog.categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Persona
            <select value={form.personId} onChange={(e) => setForm({ ...form, personId: e.target.value })}>
              <option value="">Hogar</option>
              {catalog.persons.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          {concept.behavior === 'budget' ? (
            <label className="check" style={{ alignSelf: 'end' }}>
              <input type="checkbox" checked={form.meter} onChange={(e) => setForm({ ...form, meter: e.target.checked })} />
              Medidor de consumo en la vista mensual
            </label>
          ) : null}
        </div>
        <label>
          Notas
          <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </label>
        <button type="button" className="button" onClick={() => void saveBasics()}>
          Guardar datos
        </button>

        <h3 style={{ marginTop: 26 }}>Condiciones por fecha</h3>
        <div className="timeline">
          {(detail?.timeline ?? []).map((t) => (
            <div key={t.version.id} className={t.version.state === 'inactive' ? 'inactive' : ''}>
              <strong>{t.title}</strong> <span className="muted">desde {dateLabel(t.from)}{t.to ? ` hasta ${dateLabel(t.to)}` : ' · vigente'}</span>
              <ul className="muted">
                {t.details.map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
              {versions.length > 1 ? (
                <button type="button" className="ghost" style={{ fontSize: 12 }} onClick={() => void run(() => api.deleteVersion(id, t.version.id), 'Versión eliminada')}>
                  Deshacer esta versión
                </button>
              ) : null}
            </div>
          ))}
        </div>
        <div className="inline">
          {!inactiveNow || versions.some((v) => v.validFrom > balance.today && v.state === 'active') ? (
            <button type="button" className="button primary" onClick={() => setWizard('conditions')}>
              Nuevas condiciones desde una fecha
            </button>
          ) : null}
          {inactiveNow ? (
            <button type="button" className="button primary" onClick={() => setWizard('reactivate')}>
              Reactivar desde una fecha
            </button>
          ) : (
            <button type="button" className="button" onClick={() => setDeactivate(true)}>
              Dar de baja desde una fecha
            </button>
          )}
          <button type="button" className="button" onClick={() => setAdjust(true)}>
            Ajustar un solo mes
          </button>
        </div>
        {detail?.overrides.length ? (
          <>
            <h3 style={{ marginTop: 20 }}>Ajustes de un mes</h3>
            {detail.overrides.map((o) => (
              <div className="row" key={o.id}>
                <div>
                  {periodLabel(o.period)}
                  {o.note ? <span className="muted"> · {o.note}</span> : null}
                </div>
                <div className="inline">
                  <span className="number">{eur(o.amountCents)}</span>
                  <button type="button" className="button small" onClick={() => void run(() => api.deleteOverride(o.id), 'Ajuste eliminado')}>
                    Quitar
                  </button>
                </div>
              </div>
            ))}
          </>
        ) : null}

        {hasCalendar ? (
          <>
            <h3 style={{ marginTop: 26 }}>Próximas ocurrencias</h3>
            <p className="muted">Cancelar o mover afecta sólo a esa ocurrencia y conserva las siguientes. Reorganizar crea nuevas condiciones desde esa fecha con otra referencia, desplazando las posteriores sin alterar el pasado.</p>
            {upcoming.length === 0 ? <p className="muted">Sin ocurrencias futuras en el horizonte.</p> : null}
            {upcoming.map((o) => (
              <div className="row" key={o.key}>
                <div>
                  {dateLabel(o.date)}
                  {o.status === 'moved' ? <Badge kind="warn">movida</Badge> : null}
                  {o.status === 'added' ? <Badge>añadida</Badge> : null}
                  {o.instalmentNumber ? <span className="muted"> · cuota {o.instalmentNumber}/{o.instalmentTotal}</span> : null}
                  <div className="muted">{eur(o.amountCents)} · se imputa a {periodLabel(o.period)}</div>
                </div>
                <div className="inline">
                  {o.status === 'added' ? (
                    <button type="button" className="button small" onClick={() => void run(() => api.deleteException(o.key.split(':add:')[1]!), 'Ocurrencia quitada')}>
                      Quitar
                    </button>
                  ) : (
                    <>
                      <button type="button" className="button small" onClick={() => void run(() => api.addException(id, { kind: 'cancel', originalDate: o.originalDate }), `Sesión del ${shortDateLabel(o.originalDate)} cancelada; las siguientes se conservan`)}>
                        Cancelar sólo esta
                      </button>
                      <button type="button" className="button small" onClick={() => setSession({ key: o.key, date: o.originalDate, mode: 'move' })}>
                        Mover sólo esta
                      </button>
                      {concept.behavior === 'occurrence' || (lastActive?.frequency === 'weekly' || lastActive?.frequency === 'biweekly') ? (
                        <button type="button" className="button small" onClick={() => setSession({ key: o.key, date: o.originalDate, mode: 'reorganize' })}>
                          Reorganizar desde aquí
                        </button>
                      ) : null}
                    </>
                  )}
                </div>
              </div>
            ))}
            <div className="inline" style={{ marginTop: 10 }}>
              <button type="button" className="button" onClick={() => setSession({ key: '', date: balance.today, mode: 'add' })}>
                + Añadir una ocurrencia suelta
              </button>
            </div>
            {detail?.exceptions.length ? (
              <>
                <h3 style={{ marginTop: 20 }}>Excepciones registradas</h3>
                {detail.exceptions.map((e) => (
                  <div className="row" key={e.id}>
                    <div>
                      {e.kind === 'cancel' ? 'Cancelada' : e.kind === 'move' ? `Movida a ${e.newDate}` : 'Añadida'} · {e.originalDate}
                      {e.note ? <span className="muted"> · {e.note}</span> : null}
                    </div>
                    <button type="button" className="button small" onClick={() => void run(() => api.deleteException(e.id), 'Excepción deshecha')}>
                      Deshacer
                    </button>
                  </div>
                ))}
              </>
            ) : null}
          </>
        ) : null}

        <div className="hint" style={{ marginTop: 24 }}>
          {hasAllocations ? 'Este concepto tiene movimientos imputados: no se puede borrar. Dar de baja conserva el histórico y deja de prever.' : 'Sin movimientos imputados: se puede borrar; con movimientos sólo se da de baja.'}
        </div>
        {!hasAllocations ? (
          <button type="button" className="button danger" style={{ marginTop: 12 }} onClick={() => setConfirmDelete(true)}>
            Borrar concepto
          </button>
        ) : null}
      </Modal>
      {wizard ? (
        <ConceptWizard
          mode={wizard}
          concept={concept}
          baseVersion={lastActive}
          onClose={() => setWizard(null)}
          onSaved={() => {
            setWizard(null);
            void reload();
          }}
        />
      ) : null}
      {deactivate ? <DeactivateModal concept={concept} onClose={() => setDeactivate(false)} onDone={() => { setDeactivate(false); void reload(); }} /> : null}
      {adjust ? <AdjustModal conceptId={id} onClose={() => { setAdjust(false); void reload(); }} /> : null}
      {session ? <SessionModal concept={concept} base={lastActive} session={session} onClose={() => { setSession(null); void reload(); }} /> : null}
      {confirmDelete ? (
        <Modal title="Borrar concepto" onClose={() => setConfirmDelete(false)} actions={<button type="button" className="button danger" onClick={() => void run(() => api.deleteConcept(id), 'Concepto borrado').then((r) => r !== undefined && onClose())}>Borrar</button>}>
          <p>Se borran el concepto, sus condiciones y excepciones. Sólo es posible porque no tiene movimientos imputados.</p>
        </Modal>
      ) : null}
    </>
  );
}

function DeactivateModal({ concept, onClose, onDone }: { concept: Concept; onClose: () => void; onDone: () => void }) {
  const { balance, run } = useApp();
  const [date, setDate] = useState(balance?.today ?? '');
  const [note, setNote] = useState('');
  return (
    <Modal title={`Dar de baja · ${concept.name}`} onClose={onClose} actions={<button type="button" className="button primary" onClick={() => void run(() => api.addVersion(concept.id, { validFrom: date, state: 'inactive', frequency: null, amountCents: null, schedule: {}, note: note || null }), 'Baja registrada; el histórico se conserva').then((r) => r && onDone())}>Dar de baja</button>}>
      <p className="muted">Desde esta fecha no se prevén cargos. Los movimientos y condiciones anteriores se conservan; se puede reactivar más adelante con otras condiciones.</p>
      <label>
        Baja desde
        <input data-autofocus type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </label>
      <label>
        Motivo (opcional)
        <input value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
    </Modal>
  );
}

function SessionModal({ concept, base, session, onClose }: { concept: Concept; base: ConceptVersion | null; session: { key: string; date: string; mode: 'move' | 'reorganize' | 'add' }; onClose: () => void }) {
  const { run } = useApp();
  const [newDate, setNewDate] = useState(session.date);
  const [amount, setAmount] = useState('');
  const [weekday, setWeekday] = useState<number>(base?.schedule.weekday ?? (new Date(`${session.date}T00:00:00Z`).getUTCDay() || 7));
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  async function save() {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newDate)) return setError('Fecha inválida.');
    if (session.mode === 'move') {
      const ok = await run(() => api.addException(concept.id, { kind: 'move', originalDate: session.date, newDate, note: note || null }), 'Sesión movida; las siguientes no cambian');
      if (ok) onClose();
      return;
    }
    if (session.mode === 'add') {
      const cents = amount ? Number(amount.replace(',', '.')) : null;
      const ok = await run(() => api.addException(concept.id, { kind: 'add', originalDate: newDate, amountCents: cents !== null && !Number.isNaN(cents) ? Math.round(cents * 100) : null, note: note || null }), 'Ocurrencia añadida');
      if (ok) onClose();
      return;
    }
    if (!base) return setError('No hay condiciones activas que reorganizar.');
    const schedule = { ...base.schedule };
    if (base.frequency === 'weekly') schedule.weekday = weekday;
    if (base.frequency === 'biweekly' || base.frequency === 'bimonthly' || base.frequency === 'monthly') schedule.referenceDate = newDate;
    if (base.frequency === 'monthly') schedule.dayOfMonth = Number(newDate.slice(8, 10));
    // La nueva versión rige desde la ocurrencia elegida: esa fecha y las siguientes se recalculan con la nueva referencia.
    const validFrom = newDate < session.date ? newDate : session.date;
    const ok = await run(() => api.addVersion(concept.id, { validFrom, state: 'active', frequency: base.frequency, amountCents: base.amountCents, schedule, principalCents: base.principalCents, note: note || `Reorganizado desde ${validFrom}` }), 'Recurrencia reorganizada desde la nueva referencia');
    if (ok) onClose();
  }
  const title = session.mode === 'move' ? `Mover sólo la sesión del ${session.date}` : session.mode === 'add' ? 'Añadir una ocurrencia' : `Reorganizar desde el ${session.date}`;
  return (
    <Modal title={title} onClose={onClose} error={error} actions={<button type="button" className="button primary" onClick={() => void save()}>Guardar</button>}>
      {session.mode === 'reorganize' ? (
        <>
          <p className="muted">Las ocurrencias anteriores quedan como estaban. Desde la nueva referencia se recalculan las siguientes con las mismas condiciones de precio.</p>
          {base?.frequency === 'weekly' ? (
            <label>
              Nuevo día de la semana
              <select data-autofocus value={weekday} onChange={(e) => setWeekday(Number(e.target.value))}>
                {WEEKDAY_NAMES_ES.map((n, i) => (
                  <option key={n} value={i + 1}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <label>
            {base?.frequency === 'weekly' ? 'Aplicar desde' : 'Nueva fecha de referencia (primera ocurrencia)'}
            <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} />
          </label>
        </>
      ) : (
        <>
          <label>
            {session.mode === 'move' ? 'Nueva fecha' : 'Fecha'}
            <input data-autofocus type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} />
          </label>
          {session.mode === 'add' ? (
            <label>
              Importe (€) · vacío = el de las condiciones vigentes
              <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </label>
          ) : null}
        </>
      )}
      <label>
        Nota (opcional)
        <input value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
    </Modal>
  );
}
